import React, {useState} from 'react';
import {Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconTrash} from '@tabler/icons-react';
import type {CourseAssignmentRuleResponse} from 'src/api/ProactApiClient';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  describeAudienceFacts,
  pluralizePeople,
  selectAllAudienceLabel,
  type SourceEntityNameLookup,
} from 'src/app/assignment-rules/assignmentRuleHelpers';
import {useDeleteCourseAssignmentRuleMutation} from 'src/redux/queries/courseAssignmentRule';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface AssignmentRuleCardProps {
  rule: CourseAssignmentRuleResponse;
  nameLookup: SourceEntityNameLookup;
  canEdit: boolean;
  onEdit: () => void;
}

/**
 * Rule summary as a labeled fact block (Audience / Due / N people) rather
 * than generated prose — see docs/plans/training-branch-cleanup.md WS4.
 * The edit modal's `active` switch is the sole activate/deactivate control,
 * and exceptions live there too, so this card is read-only + Edit/Delete.
 */
export const AssignmentRuleCard: React.FC<AssignmentRuleCardProps> = ({
  rule,
  nameLookup,
  canEdit,
  onEdit,
}) => {
  const {setAlert} = useAlert();
  const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false);

  const [deleteRule, {isLoading: isDeleting}] =
    useDeleteCourseAssignmentRuleMutation();

  const handleDelete = async () => {
    try {
      await deleteRule(rule.id).unwrap();
      setAlert('Assignment rule deleted.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setConfirmDeleteOpened(false);
    }
  };

  const isSelectAll = rule.selectAllEntityType != null;
  const audienceFacts = isSelectAll
    ? []
    : describeAudienceFacts(rule.sourceEntities ?? [], nameLookup);

  return (
    <Paper withBorder shadow="xs" p="md" data-testid={`assignment-rule-card-${rule.id}`}>
      <Flex justify="space-between" align="flex-start" gap="sm">
        <Box style={{flex: 1}}>
          {rule.name && (
            <Text fw={600} mb={4}>
              {rule.name}
            </Text>
          )}
          <Stack gap={2}>
            <Group gap={4}>
              <Text size="xs" fw={600} c="dimmed">
                Audience:
              </Text>
              {isSelectAll ? (
                <Badge color="gray" variant="light" size="sm">
                  {selectAllAudienceLabel(rule.selectAllEntityType!)}
                </Badge>
              ) : audienceFacts.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No one selected
                </Text>
              ) : (
                audienceFacts.map(fact => (
                  <Badge key={fact.entityType} color="gray" variant="light" size="sm">
                    {fact.label}: {fact.value}
                  </Badge>
                ))
              )}
            </Group>
            <Group gap={4}>
              <Text size="xs" fw={600} c="dimmed">
                Resolves to:
              </Text>
              <Text size="xs">{pluralizePeople(rule.resolvedCount ?? 0)}</Text>
            </Group>
          </Stack>
          {!rule.active && (
            <Badge color="yellow" size="sm" mt={4}>
              Inactive
            </Badge>
          )}
        </Box>
        <Group gap="xs" wrap="nowrap">
          {canEdit && (
            <>
              <Button size="xs" variant="subtle" onClick={onEdit}>
                Edit
              </Button>
              <ActionIcon
                tooltipText="Delete rule"
                color="red"
                onClick={() => setConfirmDeleteOpened(true)}>
                <IconTrash size={16} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Flex>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => setConfirmDeleteOpened(false)}
        onConfirm={handleDelete}
        title="Delete this rule?"
        body="People enrolled only through this rule will be disenrolled. This cannot be undone."
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
      />
    </Paper>
  );
};
